import { getApiBase } from "@app/services/apiConfig";
import apiClient from "@app/services/apiClient";

const API_BASE = getApiBase();
const MARKET_DATA_BASE = `${API_BASE}/api/dashboard/market-data`;

export type LiquidityLevel = "high" | "medium" | "low";

export type WatchlistEntry = {
  symbol: string;
  name: string;
  price: number;
  change24h: number;
  change1h: number;
  liquidity: LiquidityLevel;
  venue: string;
  pinned?: boolean;
};

export type OrderBookLevel = {
  price: number;
  size: number;
  venue: string;
};

export type TradeLogEntry = {
  id: string;
  time: string;
  side: "buy" | "sell";
  price: number;
  quantity: number;
  venue: string;
};

export type InstrumentSnapshot = {
  symbol: string;
  price: number;
  change24h: number;
  change1h?: number;
  venue: string;
  sparkline: number[];
  dailyRangePercent: number;
  orderBook: {
    bids: OrderBookLevel[];
    asks: OrderBookLevel[];
  };
  trades: TradeLogEntry[];
};

export type SentimentIndicator = {
  id: string;
  label: string;
  value: number;
  annotation?: string;
};

export type LiquidityFlowPoint = {
  symbol: string;
  netFlowUsd: number;
  direction: "inflow" | "outflow" | "neutral";
};

export type SentimentResponse = {
  indicators: SentimentIndicator[];
  liquidityFlow: LiquidityFlowPoint[];
  lastUpdatedAt?: string | null;
};

export type ClusterSettingsPayload = {
  autoAnalysisEnabled: boolean;
  waitMinutes: number;
  minArticles: number;
  keywordFilters: string[];
  telegramAlertsEnabled: boolean;
  telegramChannelId: string | null;
};

export type ClusterSettingsResponse = ClusterSettingsPayload & {
  telegramChannels: Array<{ id: string; label: string }>;
  updatedAt?: string | null;
};

export type ClusterSummaryEntry = {
  symbol: string;
  articleCount: number;
  status: "pending" | "analyzing" | "analyzed" | "failed";
  nextAutoAnalysisAt: string | null;
  latestTriggerAt: string | null;
  lastError: string | null;
};

export type ClusterArticle = {
  id: string;
  title: string;
  source: string;
  publishedAt: string;
  url?: string;
  impact: "high" | "medium" | "low";
  keywordsMatched: string[];
  summary?: string | null;
  clustered?: boolean;
  ingestedAt?: string;
};

export type ClusterDetailResponse = {
  symbol: string;
  articles: ClusterArticle[];
  aiSummary: {
    text: string;
    recommendation: "buy" | "sell" | "neutral";
    confidence: number;
    analyzedAt: string;
  } | null;
  telegram: {
    enabled: boolean;
    channelId: string | null;
    lastNotificationAt: string | null;
    lastStatus: "sent" | "skipped" | "failed" | null;
    lastError: string | null;
  } | null;
  lastUpdatedAt?: string | null;
};

export type NewsFeedItem = {
  id: string;
  title: string;
  source: string;
  publishedAt: string;
  impact: "high" | "medium" | "low";
  symbols: string[];
  summary?: string | null;
  clustered?: boolean;
  keywordsMatched?: string[];
  url?: string | null;
};

export type CryptoBubble = {
  symbol: string;
  name: string;
  price: number;
  marketCap: number;
  volume24h: number;
  change24h: number;
  rank: number | null;
};

export type CollapseSignal = {
  id: string;
  category: "on_chain" | "liquidity" | "news" | "social" | "technical" | "derivatives" | "system";
  label: string;
  severity: "critical" | "warning" | "info" | "ok";
  triggered: boolean;
  value?: string | null;
  threshold?: string | null;
  description?: string | null;
  dataSource?: string | null;
  updatedAt?: string | null;
  signalWindow?: string | null;
};

export type CollapseSourceStatus = {
  id: string;
  name: string;
  status: "online" | "degraded" | "offline" | "unknown";
  provider?: string | null;
  lastCheckedAt?: string | null;
  details?: string | null;
};

export type CollapseTriggerCondition = {
  id: string;
  title: string;
  description: string;
  severity: "critical" | "warning" | "info";
  satisfied: boolean;
  matchedSignals: string[];
  lastTriggeredAt?: string | null;
};

export type CollapseAction = {
  id: string;
  label: string;
  description: string;
  category: "trading" | "alerts" | "risk";
  status: "idle" | "running" | "cooldown";
  supported: boolean;
  isDangerous: boolean;
  lastExecutedAt?: string | null;
};

export type CollapseTimelineMetric = {
  label: string;
  value: string;
};

export type CollapseTimelineEvent = {
  id: string;
  timestamp: string;
  category: "on_chain" | "liquidity" | "news" | "social" | "technical" | "derivatives" | "system";
  severity: "critical" | "warning" | "info";
  title: string;
  summary?: string | null;
  metrics: CollapseTimelineMetric[];
  matchedSignals: string[];
};

export type CollapseMonitorSummary = {
  overallStatus: "stable" | "caution" | "critical";
  triggeredSignals: number;
  criticalSignals: number;
  warningSignals: number;
  lastUpdatedAt: string;
};

export type CollapseMonitorResponse = {
  generatedAt: string;
  signals: CollapseSignal[];
  sources: CollapseSourceStatus[];
  triggers: CollapseTriggerCondition[];
  actions: CollapseAction[];
  timeline: CollapseTimelineEvent[];
  summary: CollapseMonitorSummary;
};

export type CollapseActionRequest = {
  reason?: string;
  initiatedBy?: string;
};

export type CollapseActionResponse = {
  actionId: string;
  status: "queued" | "unsupported" | "completed";
  processedAt: string;
  message?: string;
};

/**
 * Handles HTTP response and converts to typed data.
 * Provides defensive error handling and logging.
 * @param response - Fetch Response object
 * @returns Promise resolving to typed data
 * @throws Error with descriptive message on failure
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === "string") {
        message = payload.detail;
      } else if (typeof payload?.message === "string") {
        message = payload.message;
      }
    } catch (error) {
      // Response body is not JSON, use status text if available
      if (response.statusText) {
        message = `${message}: ${response.statusText}`;
      }
    }
    
    // Log error for debugging (defensive programming)
    console.error(`API request failed: ${response.status} - ${message}`);
    throw new Error(message);
  }
  
  if (response.status === 204) {
    return undefined as T;
  }
  
  try {
    return (await response.json()) as T;
  } catch (error) {
    console.error("Failed to parse JSON response:", error);
    throw new Error("Invalid response format from server");
  }
}

export async function fetchWatchlist() {
  const response = await fetch(`${MARKET_DATA_BASE}/watchlist`);
  return handleResponse<WatchlistEntry[]>(response);
}

/**
 * Fetches instrument snapshot data for a given symbol.
 * @param symbol - Trading symbol (e.g., "BTCUSDT")
 * @returns Promise resolving to instrument snapshot data
 * @throws Error if symbol is empty or request fails
 */
export async function fetchInstrumentSnapshot(symbol: string): Promise<InstrumentSnapshot> {
  if (!symbol || symbol.trim() === "") {
    throw new Error("Symbol is required for instrument snapshot");
  }
  
  try {
    const response = await fetch(`${MARKET_DATA_BASE}/snapshot/${encodeURIComponent(symbol)}`);
    return handleResponse<InstrumentSnapshot>(response);
  } catch (error) {
    // Log error for debugging but don't expose internal details
    if (error instanceof Error) {
      console.error(`Failed to fetch instrument snapshot for ${symbol}:`, error.message);
    }
    throw error;
  }
}

export async function fetchSentimentData() {
  const response = await fetch(`${MARKET_DATA_BASE}/sentiment`);
  return handleResponse<SentimentResponse>(response);
}

export async function fetchClusterSettings() {
  const response = await fetch(`${MARKET_DATA_BASE}/cluster-settings`);
  return handleResponse<ClusterSettingsResponse>(response);
}

export async function updateClusterSettings(payload: ClusterSettingsPayload) {
  const { apiClient } = await import("./apiClient");
  const response = await apiClient.patch<ClusterSettingsResponse>(
    "/dashboard/market-data/cluster-settings",
    payload,
  );
  return response.data;
}

export async function fetchClusterSummary() {
  const response = await fetch(`${MARKET_DATA_BASE}/cluster-summary`);
  return handleResponse<ClusterSummaryEntry[]>(response);
}

export async function fetchClusterDetail(symbol: string) {
  const response = await fetch(`${MARKET_DATA_BASE}/cluster/${encodeURIComponent(symbol)}`);
  return handleResponse<ClusterDetailResponse>(response);
}

export async function triggerClusterAnalysis(symbol: string) {
  const response = await fetch(`${MARKET_DATA_BASE}/cluster/${encodeURIComponent(symbol)}/analyze`, {
    method: "POST",
  });
  return handleResponse<ClusterDetailResponse>(response);
}

export async function discardCluster(symbol: string) {
  const response = await fetch(`${MARKET_DATA_BASE}/cluster/${encodeURIComponent(symbol)}/discard`, {
    method: "POST",
  });
  return handleResponse<void>(response);
}

export async function sendClusterNotification(symbol: string) {
  const response = await fetch(`${MARKET_DATA_BASE}/cluster/${encodeURIComponent(symbol)}/notify`, {
    method: "POST",
  });
  return handleResponse<{ status: string }>(response);
}

export async function fetchNewsFeed() {
  const response = await fetch(`${MARKET_DATA_BASE}/news`);
  return handleResponse<NewsFeedItem[]>(response);
}

export async function fetchCollapseMonitor() {
  const response = await fetch(`${MARKET_DATA_BASE}/collapse-monitor`);
  return handleResponse<CollapseMonitorResponse>(response);
}

export async function executeCollapseAction(actionId: string, payload: CollapseActionRequest) {
  const response = await fetch(
    `${MARKET_DATA_BASE}/collapse-monitor/actions/${encodeURIComponent(actionId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return handleResponse<CollapseActionResponse>(response);
}

export async function fetchCryptoBubbles(symbols?: string[]) {
  const url = new URL(`${MARKET_DATA_BASE}/crypto-bubbles`);
  if (symbols && symbols.length > 0) {
    url.searchParams.set("symbols", symbols.join(","));
  }
  const response = await fetch(url.toString());
  return handleResponse<CryptoBubble[]>(response);
}

// News Services Control Types
export type NewsServicesStatus = {
  fetchingEnabled: boolean;
  filteringEnabled: boolean;
  classificationEnabled: boolean;
  aiAnalysisEnabled: boolean;
};

export type NewsServiceToggleRequest = {
  service: "fetching" | "filtering" | "classification" | "ai_analysis";
  enabled: boolean;
};

// News Services Control API
export async function fetchNewsServicesStatus() {
  const response = await apiClient.get<NewsServicesStatus>("/dashboard/market-data/news-services-status");
  return response.data;
}

export async function toggleNewsService(request: NewsServiceToggleRequest) {
  const response = await apiClient.post<NewsServicesStatus>(
    "/dashboard/market-data/news-services-toggle",
    request
  );
  return response.data;
}